/* UI-SP2-7 — SPA Legal Reviews listing page. Routed at /spa/reviews.
   Same shape as DDAssessmentsListPage but filtered to SPA module and
   re-labelled per the legal-review domain (Reviewer instead of
   Analyst, Verification instead of Classification, etc.). */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAssessments } from '../services/api';
import type { AssessmentListItem } from '../types/api';

const PAGE_SIZE = 25;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed: 'risk-low',
    analysing: 'risk-medium',
    ingesting: 'risk-medium',
    failed: 'risk-high',
    cancelled: '',
  };
  const label: Record<string, string> = {
    completed: 'Completed',
    analysing: 'Processing',
    ingesting: 'Review Pending',
    failed: 'Failed',
    cancelled: 'Cancelled',
  };
  const cls = map[status] ?? '';
  return cls ? (
    <span className={cls}>{label[status] ?? status}</span>
  ) : (
    <span className="t-muted t-xs">{label[status] ?? status}</span>
  );
}

function VerificationPill({ overall }: { overall: string | null }) {
  if (!overall) return <span className="t-muted t-xs">Pending</span>;
  const cls = overall === 'Pass'
    ? 'risk-low'
    : overall === 'Fail'
    ? 'risk-high'
    : 'risk-medium';
  const label = overall === 'Pass' ? 'Pass'
    : overall === 'Fail' ? 'Fail'
    : 'Conditional';
  return <span className={cls}>{label}</span>;
}

function buildPageRange(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const out: Array<number | 'ellipsis'> = [];
  const last = total - 1;
  const lo = Math.max(1, current - 1);
  const hi = Math.min(last - 1, current + 1);
  out.push(0);
  if (lo > 1) out.push('ellipsis');
  for (let p = lo; p <= hi; p++) out.push(p);
  if (hi < last - 1) out.push('ellipsis');
  out.push(last);
  return out;
}

export function SPAReviewsListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<AssessmentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingStatus, setPendingStatus] = useState('');
  const [pendingVerification, setPendingVerification] = useState('');
  const [pendingDate, setPendingDate] = useState('');

  useEffect(() => {
    setIsLoading(true);
    listAssessments({ module_type: 'SPA', limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch(() => setError('Failed to load SPA reviews'))
      .finally(() => setIsLoading(false));
  }, [page]);

  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (search) {
        const q = search.toLowerCase();
        const haystack = [
          it.deal_name ?? '',
          it.analyst_name ?? '',
          it.assessment_id,
        ].join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (statusFilter && it.status !== statusFilter) return false;
      if (verificationFilter && it.overall_status !== verificationFilter) return false;
      if (dateFilter && it.created_at) {
        const ageDays = (Date.now() - new Date(it.created_at).getTime()) / 86400000;
        if (dateFilter === '7d' && ageDays > 7) return false;
        if (dateFilter === '30d' && ageDays > 30) return false;
        if (dateFilter === '90d' && ageDays > 90) return false;
      }
      return true;
    });
  }, [items, search, statusFilter, verificationFilter, dateFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstShown = filteredItems.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastShown = page * PAGE_SIZE + filteredItems.length;

  function applyFilters() {
    setSearch(pendingSearch);
    setStatusFilter(pendingStatus);
    setVerificationFilter(pendingVerification);
    setDateFilter(pendingDate);
    setPage(0);
  }

  function clearFilters() {
    setPendingSearch(''); setPendingStatus(''); setPendingVerification(''); setPendingDate('');
    setSearch(''); setStatusFilter(''); setVerificationFilter(''); setDateFilter('');
    setPage(0);
  }

  return (
    <div>
      {/* UI-SP2-10 — match the DD listing layout: title + filter +
          counter all on the peach band, table on the page body. */}
      <div className="list-header-band">
        <div className="page-topbar page-topbar-hero" style={{ paddingBottom: 'var(--s3)' }}>
          <div>
            <p className="t-xs t-muted" style={{ margin: 0, marginBottom: 'var(--s2)' }}>
              <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }} style={{ color: 'var(--muted)', textDecoration: 'none' }}>
                Dashboard
              </a>
              {' › '}
              <span style={{ color: 'var(--text-sec)' }}>SPA Reviews</span>
            </p>
            <h1 className="page-topbar-title">SPA Legal Reviews</h1>
            <p className="page-topbar-sub">Complete archive of all agreement reviews and legal assessments</p>
          </div>
          <div className="topbar-actions">
            <button className="btn btn-accent" type="button" onClick={() => navigate('/spa/upload')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Review
            </button>
          </div>
        </div>

        <div className="list-header-filter-row">
        <div className="filter-strip">
          <div className="filter-search">
            <svg className="filter-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="search"
              className="filter-search-input"
              placeholder="Search by deal name, reviewer, or job ID..."
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
            />
          </div>
          <select className="filter-select" value={pendingStatus} onChange={(e) => setPendingStatus(e.target.value)} aria-label="Status filter">
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="analysing">Processing</option>
            <option value="ingesting">Review Pending</option>
            <option value="failed">Failed</option>
          </select>
          <select className="filter-select" value={pendingVerification} onChange={(e) => setPendingVerification(e.target.value)} aria-label="Verification filter">
            <option value="">All Verifications</option>
            <option value="Pass">Pass</option>
            <option value="Watch">Conditional</option>
            <option value="Fail">Fail</option>
          </select>
          <select className="filter-select" value={pendingDate} onChange={(e) => setPendingDate(e.target.value)} aria-label="Date filter">
            <option value="">All Dates</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button type="button" className="btn btn-secondary btn-sm" onClick={clearFilters}>Clear</button>
          <button type="button" className="btn btn-accent btn-sm" onClick={applyFilters}>Apply</button>
          {/* Sprint 5 client feedback 2026-07-08 (UI Ticket — Reports
              Export removal) — non-functional Export button removed
              (matches DDAssessmentsListPage). */}
        </div>

        <p className="t-xs t-muted" style={{ margin: 'var(--s2) 0 var(--s4)' }}>
          {isLoading ? 'Loading…' : `Showing ${filteredItems.length} of ${total} reviews`}
        </p>
        </div>
      </div>

      <div className="page-body">
        {error && (
          <div className="alert alert-fail" style={{ marginBottom: 'var(--s4)' }}>{error}</div>
        )}

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s10)' }}>
              <span className="spinner spinner-lg" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div style={{ padding: 'var(--s10)', textAlign: 'center' }}>
              <p className="t-muted t-sm">No SPA reviews match your filters.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data-table" aria-label="SPA Legal Reviews">
                <thead>
                  <tr>
                    {['Deal Name', 'Docs', 'Reviewer', 'Submitted', 'Status', 'Verification', 'Actions'].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr
                      key={item.assessment_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/assessments/${item.assessment_id}`)}
                    >
                      <td style={{ maxWidth: 240 }}>
                        <div className="td-name" style={{ fontWeight: 600 }}>
                          {item.deal_name ?? <span className="t-muted">—</span>}
                        </div>
                        <div className="t-mono t-xs" style={{ color: 'var(--muted)', marginTop: 2 }}>
                          {item.assessment_id}
                        </div>
                      </td>
                      <td className="t-xs" style={{ textAlign: 'center' }}>{item.document_count}</td>
                      <td>{item.analyst_name ?? <span className="t-muted">—</span>}</td>
                      <td style={{ whiteSpace: 'nowrap' }} className="t-xs">{formatDate(item.created_at)}</td>
                      <td><StatusPill status={item.status} /></td>
                      <td><VerificationPill overall={item.overall_status} /></td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="row-actions">
                          <button
                            type="button"
                            className="row-action-btn"
                            data-tip="View review"
                            aria-label="View review"
                            onClick={() => navigate(`/assessments/${item.assessment_id}`)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                          <button
                            type="button"
                            className="row-action-btn"
                            data-tip="Download DOCX"
                            aria-label="Download DOCX"
                            onClick={() => navigate(`/assessments/${item.assessment_id}?download=docx`)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && total > PAGE_SIZE && (
            <div className="table-pager">
              <span className="t-xs t-muted">
                Showing {firstShown}-{lastShown} of {total}
              </span>
              <div className="pager-controls">
                <button
                  type="button"
                  className="pager-btn pager-icon"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  aria-label="Previous page"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                {buildPageRange(page, totalPages).map((token, i) =>
                  token === 'ellipsis' ? (
                    <span key={`e-${i}`} className="pager-ellipsis" aria-hidden="true">…</span>
                  ) : (
                    <button
                      key={token}
                      type="button"
                      className={`pager-btn pager-num${token === page ? ' active' : ''}`}
                      onClick={() => setPage(token)}
                      aria-current={token === page ? 'page' : undefined}
                      aria-label={`Page ${token + 1}`}
                    >
                      {token + 1}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  className="pager-btn pager-icon"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  aria-label="Next page"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
